import {
	Link,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { EditorContent } from "@tiptap/react";
import { motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { CertificateEmitDialog } from "@/dialogs/certificate-emit-dialog";
import { useEditor } from "@/editor/editor";
import { cn } from "@/utils";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import type { courseLesson_lesson$key } from "./__generated__/courseLesson_lesson.graphql";
import type { courseLessonCompleteLessonMutation } from "./__generated__/courseLessonCompleteLessonMutation.graphql";
import type { courseLessonUncompleteLessonMutation } from "./__generated__/courseLessonUncompleteLessonMutation.graphql";
import type { CommentOrdering } from "./__generated__/lessonCommentsQuery.graphql";
import { Attachment } from "./attachment";
import { LessonComments } from "./course-lesson-comments";
import { CourseVideo } from "./course-video";

export function CourseLesson({ lesson }: { lesson: courseLesson_lesson$key }) {
	const [autoSkipTimeout, setAutoSkipTimeout] = useState(5);
	const [autoSkipOn, setAutoSkipOn] = useState(false);
	const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);

	const navigate = useNavigate({ from: "/cursos/$course/$lesson" });
	const { autoPlay } = useSearch({
		from: "/_visibility/_standalone/cursos/$course/$lesson",
	});
	const { club, session } = useRouteContext({ from: "__root__" });
	const { track } = useAnalytics();
	const { comment_order: commentOrder } = useSearch({
		from: "/_visibility/_standalone/cursos/$course/$lesson",
	});

	const data = useFragment<courseLesson_lesson$key>(
		graphql`
	    fragment courseLesson_lesson on CourseContent {
				id

        name
        description
        completed

        section {
          id
        }

        course {
          slug
          progress
          completed
          ...certificateEmitDialogFragment
        }

        ...courseVideo

        video {
          __typename
        }

        nextClass {
          url
          slug
          canAccess
        }
        previousClass {
          url
          slug
          canAccess
        }
        attachments {
          id
          size
          originalName
          signedUrl
        }

        ...courseLessonComments_lesson
       }
	`,
		lesson,
	);

	const [complete, isPendingComplete] =
		useMutation<courseLessonCompleteLessonMutation>(graphql`
	    mutation courseLessonCompleteLessonMutation($id: ID!, $username: String!) {
				completeCourseClass(class: $id) {
					...cursos_lesson @arguments(username: $username)

					completed

					course {
					  completed
						certificateEmission
						certificate {
							id
						}
					}

					nextClass {
					  canAccess
					}
				}
			}
	`);
	const [uncomplete, isPendingUncomplete] =
		useMutation<courseLessonUncompleteLessonMutation>(graphql`
	    mutation courseLessonUncompleteLessonMutation($id: ID!, $username: String!) {
				uncompleteCourseClass(class: $id) {
					...cursos_lesson @arguments(username: $username)

					completed
				}
			}
	`);

	const orderBy: CommentOrdering = useMemo(() => {
		switch (commentOrder) {
			case "newest":
				return "NEWEST";
			case "oldest":
				return "OLDEST";
			default:
				return "TRENDING";
		}
	}, [commentOrder]);

	const commentOrderingToParam = (val: CommentOrdering) => {
		switch (val) {
			case "NEWEST":
				return "newest";
			case "OLDEST":
				return "oldest";
			default:
				return "trending";
		}
	};

	const editor = useEditor({
		editable: false,
		content: data.description,
		features: {
			placeholder: { enabled: true },
			colors: { enabled: true },
			attachments: { enabled: true },
			video: { enabled: true },
			poll: { enabled: true },
			youtube: { enabled: true },
			image: { enabled: true },
			iframe: { enabled: true },
			button: { enabled: true },
			mention: { enabled: true },
			styling: { enabled: true },
		},
		club: club.slug,
	});

	useEffect(() => {
		if (editor) {
			editor.commands.setContent(data.description);
		}
	}, [data.description, editor]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: i want to run when lesson changes
	useEffect(() => {
		setAutoSkipOn(false);
		setAutoSkipTimeout(5);
	}, [data.id]);

	useEffect(() => {
		if (autoSkipOn && data.nextClass?.slug) {
			const timeout = setTimeout(() => {
				navigate({
					to: "/cursos/$course/$lesson",
					params: {
						course: data.course.slug,
						lesson: data.nextClass?.slug,
					},
					search: {
						autoPlay: true,
					},
				});
			}, 10500);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [
		autoSkipOn,
		data.nextClass,
		data.nextClass?.slug,
		data.course.slug,
		navigate,
	]);

	useEffect(() => {
		if (autoSkipOn && autoSkipTimeout > 0) {
			const timeout = setTimeout(() => {
				setAutoSkipTimeout((s) => s - 1);
			}, 1000);

			return () => {
				clearTimeout(timeout);
			};
		} else if (!autoSkipOn && autoSkipTimeout < 5) {
			setAutoSkipTimeout(5);
		}
	}, [autoSkipOn, autoSkipTimeout]);

	return (
		<Dialog>
			<CertificateEmitDialog
				open={certificateDialogOpen}
				onClose={() => setCertificateDialogOpen(false)}
				course={data.course}
			/>

			<div className="w-full flex flex-col gap-6">
				<div className="max-md:px-3 px-0 w-full">
					<Card
						className={cn(
							"flex-1 w-full max-lg:bg-background overflow-hidden h-fit pt-0 max-lg:shadow-none max-lg:border-0",
						)}
					>
						{data.video?.__typename ? (
							<div className="relative">
								<CourseVideo
									course={data}
									hideControls={autoSkipOn}
									autoPlay={autoPlay}
									onEnded={() => {
										setAutoSkipOn(true);
									}}
								/>

								{autoSkipOn && (
									<div className="bg-black/60 z-2 inset-0 size-full absolute flex flex-col items-center justify-center">
										<div className="flex-1 h-full" />

										<div className="flex bg-black w-full gap-3 p-3 h-fit flex-row items-center justify-end">
											<Button
												type="button"
												variant="outline"
												onClick={() => setAutoSkipOn(false)}
											>
												Cancelar
											</Button>

											<Button
												type="button"
												className="relative overflow-hidden"
											>
												<motion.div
													className="absolute bg-accent left-0 h-full"
													initial={{ width: 0 }}
													animate={{ width: "100%" }}
													transition={{ duration: 6 }}
												/>

												<span className="z-2">
													Próxima Aula ({Math.max(autoSkipTimeout, 0)})
												</span>
											</Button>
										</div>
									</div>
								)}
							</div>
						) : null}

						<div className="px-6 max-lg:px-0">
							{!data.video?.__typename ? (
								<>
									<div className="font-semibold text-2xl/6 mt-6">
										{data.name}
									</div>

									{!editor?.isEmpty ? (
										<div className="w-full mt-6">
											<EditorContent editor={editor} />
										</div>
									) : (
										<div className="text-sm text-muted-foreground mt-6">
											Nenhuma descrição disponível
										</div>
									)}

									<Separator className="my-6" />
								</>
							) : null}

							<div className="grid grid-cols-3 gap-3">
								{data.previousClass?.canAccess ? (
									<div className="flex flex-row items-center justify-start">
										<Link
											to="/cursos/$course/$lesson"
											params={{
												course: data.course.slug,
												lesson: data.previousClass?.slug,
											}}
										>
											<Button
												variant="ghost"
												className="flex flex-row items-center has-[>svg]:pl-1"
											>
												<Icon name="ChevronLeft" className="size-4" />

												<span className="max-lg:hidden">Aula Anterior</span>
												<span className="lg:hidden">Anterior</span>
											</Button>
										</Link>
									</div>
								) : (
									<div className="flex flex-row items-center justify-start">
										<Button
											variant="ghost"
											className="flex flex-row items-center has-[>svg]:pl-1"
											disabled
										>
											<Icon name="ChevronLeft" className="size-4" />

											<span className="max-lg:hidden">Aula Anterior</span>
											<span className="lg:hidden">Anterior</span>
										</Button>
									</div>
								)}

								{data.completed ? (
									<div className="flex flex-row items-center justify-center">
										<Button
											className="flex flex-row items-center justify-center relative"
											onClick={() =>
												uncomplete({
													variables: {
														id: data.id,
														username: session?.user?.username!,
													},
													onCompleted: () => {
														track(AnalyticsEvents.COURSE_LESSON_UNCOMPLETED, {
															lesson_id: data.id,
															course_slug: data.course.slug,
														});
													},
												})
											}
											disabled={isPendingUncomplete}
										>
											{isPendingUncomplete ? (
												<Spinner />
											) : (
												<Icon name="Check" className="size-4" />
											)}
											Concluída
										</Button>
									</div>
								) : (
									<div className="flex flex-row items-center justify-center">
										<Button
											variant="outline"
											className="flex flex-row items-center"
											disabled={isPendingComplete}
											onClick={() =>
												complete({
													variables: {
														id: data.id,
														username: session?.user?.username!,
													},
													onCompleted: (response) => {
														track(AnalyticsEvents.COURSE_LESSON_COMPLETED, {
															lesson_id: data.id,
															course_slug: data.course.slug,
														});

														if (
															response.completeCourseClass.course.completed &&
															response.completeCourseClass.course
																.certificateEmission &&
															!response.completeCourseClass.course.certificate
														) {
															setCertificateDialogOpen(true);
														} else if (
															response.completeCourseClass.nextClass?.canAccess
														) {
															setAutoSkipOn(true);
														}
													},
												})
											}
										>
											{isPendingComplete && <Spinner />}

											<span className="hidden lg:block">
												Marcar como Concluído
											</span>

											<span className="lg:hidden">Concluir</span>
										</Button>
									</div>
								)}

								{data.nextClass?.canAccess ? (
									<div className="flex flex-row items-center justify-end">
										<Link
											to="/cursos/$course/$lesson"
											params={{
												course: data.course.slug,
												lesson: data.nextClass.slug,
											}}
										>
											<Button
												variant="ghost"
												className="flex flex-row items-center has-[>svg]:pr-1"
											>
												<span className="max-lg:hidden">Próxima Aula</span>
												<span className="lg:hidden">Próxima</span>
												<Icon name="ChevronRight" className="size-4" />
											</Button>
										</Link>
									</div>
								) : (
									<Button
										variant="ghost"
										className="flex flex-row items-center has-[>svg]:pr-1"
										disabled
									>
										<span className="max-lg:hidden">Próxima Aula</span>
										<span className="lg:hidden">Próxima</span>
										<Icon name="ChevronRight" className="size-4" />
									</Button>
								)}
							</div>

							{data.video?.__typename ? (
								<>
									<Separator className="mt-6" />
									<div className="font-semibold text-2xl/6 mt-6 mb-6 last:mb-0">
										{data.name}
									</div>

									{!editor?.isEmpty ? (
										<EditorContent editor={editor} />
									) : (
										<div className="text-sm text-muted-foreground">
											Nenhuma descrição disponível
										</div>
									)}
								</>
							) : null}

							{!!data.attachments.length && (
								<>
									<Separator className="mt-6" />
									<div className="flex flex-col w-full gap-1 mt-6">
										<div className="leading-snug">
											Anexos ({data.attachments.length})
										</div>

										<div className="flex flex-wrap w-full mt-3 gap-3">
											{data.attachments.map((at) => (
												<Attachment
													key={at.id}
													id={at.id}
													name={at.originalName}
													size={at.size || undefined}
													url={at.signedUrl}
												/>
											))}
										</div>
									</div>
								</>
							)}
						</div>
					</Card>
				</div>

				<LessonComments
					lesson={data}
					orderBy={orderBy}
					setOrderBy={(val: CommentOrdering) =>
						navigate({
							search: (s) => ({
								...s,
								comment_order: commentOrderingToParam(val),
							}),
						})
					}
				/>
			</div>
		</Dialog>
	);
}
